import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  MoreHorizontal,
  Sun,
  Moon,
  Monitor,
  Globe,
  HelpCircle,
  Check,
  Calculator,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { TopBarUserEntry } from './TopBarUserEntry';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveTeamId } from '@/contexts/TeamContext';
import { useCurrentUserProfile } from '@/hooks/useUserProfile';
import { useScrollShadow } from '@/hooks/useScrollShadow';
import { cn } from '@/lib/utils';
import { NotepadButton } from '@/components/notepad/NotepadButton';
import { NotepadDrawer } from '@/components/notepad/NotepadDrawer';
import { GlobalSearchTriggerMobile } from '@/features/search';
import { useQuickCalculator } from '@/features/calculator';
import { useQuickCalculatorEnabled } from '@/features/settings/useFeatureFlags';

interface TopBarMobileProps {
  title?: string;
  onOpenNav?: () => void;
}

/**
 * Mobile-optimized TopBar component
 *
 * Features:
 * - Compact 56px height (h-14) with 44px touch targets (h-11 w-11)
 * - Sticky with scroll shadow after 2px
 * - iOS safe area support (pt-[env(safe-area-inset-top)])
 * - Centered page title with truncation
 * - Kebab menu (⋯) for quick actions
 * - User avatar menu
 * - No horizontal overflow at 320px
 *
 * @example
 * <TopBarMobile title="Dashboard" onOpenNav={toggleNav} onOpenAccount={toggleAccount} />
 */
export function TopBarMobile({ title = 'modcar', onOpenNav }: TopBarMobileProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { t, i18n } = useTranslation('common');
  const { hasShadow } = useScrollShadow(2);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const teamId = useActiveTeamId();
  const quickCalculatorEnabled = useQuickCalculatorEnabled(teamId);
  const { open: openCalculator } = useQuickCalculator();
  const { data: userProfile } = useCurrentUserProfile();

  // Get firstName from profile, with fallback to legacy fullName
  const firstName = userProfile?.firstName || userProfile?.fullName?.split(' ')[0] || null;

  return (
    <header
      className={cn(
        'sticky top-0 z-40 h-14 flex items-center gap-2 px-3 sm:px-4',
        'pt-[env(safe-area-inset-top)] sm:pt-0',
        'backdrop-blur supports-[backdrop-filter]:bg-background/70',
        hasShadow ? 'shadow-sm border-b' : ''
      )}
      aria-label={t('accessibility.topNavigation')}
    >
      <div className="flex items-center justify-between gap-2 w-full">
        {/* Left: Hamburger Menu */}
        <Button
          variant="ghost"
          className="h-11 w-11 shrink-0"
          aria-label={t('accessibility.openMenu')}
          onClick={onOpenNav}
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Center: Page Title */}
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-medium text-center truncate">{title}</h1>
        </div>

        {/* Right: Search + Calculator + Notepad + Kebab Menu (Quick Actions) + User Avatar */}
        {/* On very small screens (< 375px), hide calculator to prevent overflow */}
        <div className="flex items-center gap-0.5 xs:gap-1 shrink-0">
          {/* Mobile Search Button */}
          <GlobalSearchTriggerMobile />

          {/* Quick Calculator Button - hidden on very small screens, available in kebab menu */}
          {quickCalculatorEnabled && (
            <Button
              variant="ghost"
              className="h-11 w-11 shrink-0 hidden xs:flex"
              aria-label={t('calculator.trigger', 'Snelle calculator')}
              onClick={openCalculator}
            >
              <Calculator className="h-5 w-5" />
            </Button>
          )}

          {/* Notepad Button */}
          <NotepadButton />

          {/* Kebab Menu (⋯) */}
          <Sheet open={quickActionsOpen} onOpenChange={setQuickActionsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                className="h-11 w-11 shrink-0"
                aria-label={t('accessibility.openQuickActions')}
              >
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="pb-[env(safe-area-inset-bottom)]">
              <SheetHeader>
                <SheetTitle>{t('quickActions', 'Quick Actions')}</SheetTitle>
                <SheetDescription className="sr-only">
                  Quick settings and actions menu
                </SheetDescription>
              </SheetHeader>
              <div className="py-2">
                {/* Theme Section */}
                <div className="px-3 py-2">
                  <div className="text-sm font-medium mb-2">Thema</div>
                  <div className="text-xs text-muted-foreground mb-3">
                    Actief: {resolvedTheme === 'dark' ? 'Donker' : 'Licht'}
                    {theme === 'system' && ' (Volgt systeem)'}
                  </div>

                  {/* Theme Options */}
                  <div className="space-y-1">
                    {/* Light */}
                    <button
                      type="button"
                      onClick={() => {
                        setTheme('light');
                        setQuickActionsOpen(false);
                      }}
                      className="w-full h-12 px-3 rounded-md text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 flex items-center justify-between"
                      aria-label={t('accessibility.setLightTheme')}
                    >
                      <div className="flex items-center gap-3">
                        <Sun className="h-5 w-5 shrink-0" />
                        <span>Licht</span>
                      </div>
                      {theme === 'light' && <Check className="h-4 w-4" />}
                    </button>

                    {/* Dark */}
                    <button
                      type="button"
                      onClick={() => {
                        setTheme('dark');
                        setQuickActionsOpen(false);
                      }}
                      className="w-full h-12 px-3 rounded-md text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 flex items-center justify-between"
                      aria-label={t('accessibility.setDarkTheme')}
                    >
                      <div className="flex items-center gap-3">
                        <Moon className="h-5 w-5 shrink-0" />
                        <span>Donker</span>
                      </div>
                      {theme === 'dark' && <Check className="h-4 w-4" />}
                    </button>

                    {/* System */}
                    <button
                      type="button"
                      onClick={() => {
                        setTheme('system');
                        setQuickActionsOpen(false);
                      }}
                      className="w-full h-12 px-3 rounded-md text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 flex items-center justify-between"
                      aria-label={t('accessibility.setSystemTheme')}
                    >
                      <div className="flex items-center gap-3">
                        <Monitor className="h-5 w-5 shrink-0" />
                        <span>Systeem</span>
                      </div>
                      {theme === 'system' && <Check className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="h-px bg-border my-2" />

                {/* Calculator - shown in menu on very small screens where button is hidden */}
                {quickCalculatorEnabled && (
                  <button
                    type="button"
                    onClick={() => {
                      openCalculator();
                      setQuickActionsOpen(false);
                    }}
                    className="w-full h-12 px-3 rounded-md text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 flex items-center gap-3 xs:hidden"
                    aria-label={t('calculator.trigger', 'Snelle calculator')}
                  >
                    <Calculator className="h-5 w-5 shrink-0" />
                    <span>{t('calculator.title', 'Calculator')}</span>
                  </button>
                )}

                {/* Language Selector */}
                <div className="h-12 flex items-center px-3">
                  <Globe className="h-5 w-5 shrink-0 mr-3" />
                  <span className="mr-3">{t('language', 'Language')}</span>
                  <select
                    value={i18n.language}
                    onChange={(e) => {
                      void i18n.changeLanguage(e.target.value);
                      setQuickActionsOpen(false);
                    }}
                    className="flex-1 bg-transparent border-none focus:outline-none focus:ring-2 rounded"
                    aria-label={t('accessibility.selectLanguage')}
                  >
                    <option value="en">English</option>
                    <option value="nl">Nederlands</option>
                  </select>
                </div>

                {/* Help */}
                <a
                  href="/help"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/help');
                    setQuickActionsOpen(false);
                  }}
                  className="block w-full h-12 px-3 rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2"
                  aria-label={t('accessibility.openHelp')}
                >
                  <div className="flex items-center gap-3 h-full">
                    <HelpCircle className="h-5 w-5 shrink-0" />
                    <span>{t('help', 'Help')}</span>
                  </div>
                </a>
              </div>
            </SheetContent>
          </Sheet>

          {/* User Avatar - Unified menu */}
          <TopBarUserEntry user={user} firstName={firstName} />
        </div>
      </div>

      {/* Notepad Drawer (global) */}
      <NotepadDrawer />
    </header>
  );
}

export default TopBarMobile;
